import {
  DatePicker,
  Field,
  FieldError,
  FieldLabel,
  Textarea,
} from "@/components/ui";

import { TRACKERS } from "@/constants/Trackers";
import { SubmitCancelButtons } from "@/features/Patient/MedicalRecords/components/SubmitCancelButtons";
import { trackEvent } from "@/lib/tracking";
import type { MedicationStatement } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, useFormState } from "react-hook-form";
import type { z } from "zod";
import { useUpdateMedication } from "../../network/useMedications";
import { updateMedicationFormSchema } from "../../schemas/schemas";

interface UpdateMedicationFormProps {
  medicationStatement: MedicationStatement;
  patientId: string;
}

export const UpdateMedicationForm = ({
  medicationStatement,
  patientId,
}: UpdateMedicationFormProps) => {
  const { mutate: saveMedication, isPending: isSaving } = useUpdateMedication({
    patientId,
    medicationStatementId: medicationStatement.id as string,
  });

  const form = useForm<z.infer<typeof updateMedicationFormSchema>>({
    resolver: zodResolver(updateMedicationFormSchema),
    defaultValues: {
      startDate: medicationStatement.safeStartDate
        ? new Date(`${medicationStatement.safeStartDate}T12:00:00Z`)
        : undefined,
      endDate: medicationStatement.safeEndDate
        ? new Date(`${medicationStatement.safeEndDate}T12:00:00Z`)
        : undefined,
      notes: medicationStatement.statementNote || "",
    },
  });

  const { isDirty } = useFormState({ control: form.control });

  const handleSubmit = (values: z.infer<typeof updateMedicationFormSchema>) => {
    saveMedication({
      startDate: values.startDate,
      endDate: values.endDate,
      note: values.notes,
    });
  };

  return (
    <form
      onSubmit={form.handleSubmit(handleSubmit)}
      className="flex flex-col gap-3"
    >
      <div className="flex-1 grid grid-cols-2 gap-4">
        <Controller
          control={form.control}
          name="startDate"
          render={({ field, fieldState }) => (
            <Field
              className="w-full flex flex-1 flex-col"
              data-invalid={fieldState.invalid}
            >
              <DatePicker
                date={field.value}
                setDate={(date) => {
                  field.onChange(new Date(date.toUTCString()));
                }}
                dateFormat="MM-dd-yyyy"
                closeOnSelect={true}
                label="Start Date"
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
        <Controller
          control={form.control}
          name="endDate"
          render={({ field, fieldState }) => (
            <Field
              className="w-full flex flex-1 flex-col"
              data-invalid={fieldState.invalid}
            >
              <DatePicker
                date={field.value}
                setDate={(date) => {
                  field.onChange(new Date(date.toUTCString()));
                }}
                dateFormat="MM-dd-yyyy"
                closeOnSelect={true}
                label="End Date"
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
        <Controller
          control={form.control}
          name="notes"
          render={({ field, fieldState }) => (
            <Field
              className="w-full flex flex-1 flex-col col-span-2"
              data-invalid={fieldState.invalid}
            >
              <FieldLabel htmlFor="notes">Additional Notes</FieldLabel>
              <Textarea
                {...field}
                id="notes"
                placeholder="Enter notes"
                aria-invalid={fieldState.invalid}
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
      </div>
      {isDirty && (
        <SubmitCancelButtons
          isSaving={isSaving}
          onCancel={() => {
            trackEvent(TRACKERS.CANCEL_CHANGES_TO_MEDICATION);
            form.reset({
              startDate: medicationStatement.safeStartDate
                ? new Date(`${medicationStatement.safeStartDate}T12:00:00Z`)
                : undefined,
              endDate: medicationStatement.safeEndDate
                ? new Date(`${medicationStatement.safeEndDate}T12:00:00Z`)
                : undefined,
              notes: medicationStatement.statementNote || "",
            });
          }}
        />
      )}
    </form>
  );
};
